import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAiStore } from '@/stores/ai';

/**
 * Route-aware context reset for the AI side panel.
 *
 * On every route change the page-specific context is reset, so the panel shows a "no connection to
 * this page" notice until the incoming page registers its own context via aiStore.enableSidePanel()
 * in its mounted() hook. Without it, a page that registers nothing inherits the previous page's
 * context and the assistant answers about the page the user has left.
 *
 * Sub-navigation within a page's own child routes (e.g. Clustering sub-tabs) is
 * intentionally excluded from the reset: if the route we came FROM is still
 * present as an ancestor in the matched hierarchy of the route we're going TO,
 * the same component is still mounted and context is still valid.
 */

// Shared by both installers below. `toMatched` is the incoming route's matched hierarchy.
function shouldResetContext(aiStore, toMatched, fromName) {
  // On initial page load the router resolves from undefined → actual route name.
  // There is no previous page to reset away from, so skip — the mounting page will
  // register its own context/tools factory via its mounted() hook.
  if (!fromName) return false;

  const toMatchedNames = new Set(toMatched.map(r => r.name).filter(Boolean));

  // Case 1: FROM route is an ancestor of TO route (parent → child navigation).
  if (toMatchedNames.has(fromName)) return false;

  // Case 2: sibling sub-tab navigation (e.g. Clustering's comparison ↔ deviation).
  // When a page registers context it records an anchorRouteName — the named route of
  // the parent component that stays mounted across all its child tabs. If that anchor
  // is still present in the TO route's matched hierarchy, the component is still mounted
  // and context is still valid.
  const anchorRoute = aiStore.sidePanel.anchorRouteName;
  if (anchorRoute && toMatchedNames.has(anchorRoute)) return false;

  return true;
}

/**
 * Call this once inside AiSidePanel.vue, which is mounted in App.vue standalone.
 */
export function useAiRouteContextReset() {
  const aiStore = useAiStore();
  const route = useRoute();

  // Watch the route NAME as a getter — this gives proper string snapshots for
  // oldValue/newValue. Watching the reactive `route` object directly is unreliable
  // because both `to` and `from` args point to the same already-mutated proxy,
  // making old === new every time.
  watch(
    () => route.name,
    (_toName, fromName) => {
      // At callback time, route.matched already reflects the incoming route.
      if (shouldResetContext(aiStore, route.matched, fromName)) aiStore.resetPageContext();
    }
  );
}

/**
 * The same reset, driven off the router rather than a mounted component. Embedded in the One Retail
 * shell AiSidePanel.vue is not rendered — the shell owns the single assistant - ADR-012 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/012-ai-assistant-extraction.md
 * and calls AO's provider, which reads this very context — so the reset has to be installed by
 * create-app.js instead. `afterEach` runs on navigation confirm, before the incoming page mounts
 * and registers its own context.
 *
 * @param {import('vue-router').Router} router
 */
export function installAiRouteContextReset(router) {
  const aiStore = useAiStore();
  router.afterEach((to, from) => {
    if (shouldResetContext(aiStore, to.matched, from.name)) aiStore.resetPageContext();
  });
}
